import React, { useState, useMemo } from "react";
import { useModalStack } from "../../utils/modalContext";
import { fetchItems } from "../../utils/fetchItemData";

export function ItemCrafting({ item }) {
  const recipes = item.recipes || [];
  const [selectedRecipeIndex, setSelectedRecipeIndex] = useState(0);
  const [craftAmount, setCraftAmount] = useState(1);
  const [energyPrice, setEnergyPrice] = useState(50);
  const [mpfee, setMPFee] = useState(1);
  const { pushModal } = useModalStack();

  const handleOpenModal = async (itemId) => {
    try {
      const itemData = await fetchItems({itemId});
      if (!itemData || !itemData[0]) return;

      pushModal({
        id: `item-${itemId}`,
        type: "itemModal",
        props: {
          item: itemData[0],
          onClose: () => {},
          showNavigation: false,
          onNavigate: null,
          totalItems: 1,
          index: null,
        },
      });
    } catch (err) {
      console.error("Failed to fetch item:", err);
    }
  };

  const selectedRecipe = recipes[selectedRecipeIndex];

  const calculated = useMemo(() => {
    if (!selectedRecipe) return null;

    const multiplier = craftAmount;

    const items = selectedRecipe.ingredients.map((item) => {
      return {
        id: item.id,
        name: item.name,
        image: item.image,
        quantity: item.quantity * multiplier,
        unitCost: item.totalCost / item.quantity,
        totalCost: item.totalCost * multiplier,
      };
    });

    const energyCost = (selectedRecipe.energyCost / 50) * multiplier;
    const energyCostTotal = energyCost * energyPrice;
    const totalItemCost = items.reduce((sum, i) => sum + i.totalCost, 0);
    const totalCost = totalItemCost + energyCostTotal;
    const marketCost = item.baseMarketPrice * selectedRecipe.quantityProduced;
    const totalProfit = marketCost * multiplier - (totalCost + (totalCost * (mpfee / 100)));

    return {
      items,
      energyCost,
      energyCostTotal,
      totalItemCost: totalItemCost,
      totalCost: totalCost,
      marketCost,
      totalProfit,
    };
  }, [selectedRecipe, craftAmount, energyPrice, item, mpfee]);

  const details = useMemo(() => {
    if (!selectedRecipe) return null;

    return {
      level: selectedRecipe.level,
      craftingTime: selectedRecipe.craftingTime,
      skill: selectedRecipe.skill,
      exp: selectedRecipe.exp,
      expType: selectedRecipe.expType,
      requiredMemberships: selectedRecipe.requiredMemberships.length > 0 ? selectedRecipe.requiredMemberships : ["F2P/VIP/LO"],
    };
  }, [selectedRecipe]);

  if (!recipes.length) {
    return (
      <div className="p-4 max-w-xl mx-auto bg-white rounded-2xl shadow text-center">
        <p className="text-red-600 font-semibold">This item cannot be crafted.</p>
      </div>
    );
  }

  return (
    <div className="wiki-crafting">
      <div className="space-y-2">
        <label className="block font-semibold">Select Recipe</label>
        <select className="w-full p-2 select-forms" value={selectedRecipeIndex} onChange={(e) => setSelectedRecipeIndex(Number(e.target.value))}>
          {recipes.map((recipe, index) => (
            <option key={recipe.id} value={index}>
              {recipe.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <label className="block font-semibold">Amount</label>
          <input type="number" value={craftAmount} onChange={(e) => setCraftAmount(Number(e.target.value))} className="w-full p-2  select-forms" min="1" />
        </div>
        <div className="flex-1">
          <label className="block font-semibold">Energy Price</label>
          <input type="number" value={energyPrice} onChange={(e) => setEnergyPrice(Number(e.target.value))} className="w-full p-2  select-forms" min="1" />
        </div>
        <div className="flex-1">
          <label className="block font-semibold">MP Fee (%)</label>
          <input type="number" value={mpfee} onChange={(e) => setMPFee(Number(e.target.value))} className="w-full p-2  select-forms" min="1" />
        </div>
      </div>

      <div>
        <h3 className="font-bold text-lg mt-4">Details</h3>
        <div className="material-list grid grid-flow-col grid-rows-4 gap-2">
          <p>
          <span className="item-title">Requires:</span> {details.requiredMemberships.map((membership) => membership.toUpperCase()).join(', ')}
          </p>
          <p><span className="item-title">Required Level:</span>  {details.level || 0} {details.skill}</p>
          <p><span className="item-title">EXP Gain:</span>  {details.exp * craftAmount} {details.expType}</p>
          <p><span className="item-title">Time:</span>  {(details.craftingTime * craftAmount).toLocaleString()} minutes</p>
          <p><span className="item-title">Cost/Energy:</span> {(calculated.totalCost / calculated.energyCost).toLocaleString()}</p>
          <p><span className="item-title">Profit/Energy:</span> {((calculated.totalProfit) / calculated.energyCost).toLocaleString()}</p>
          <p><span className="item-title">Cost/Exp:</span> {(calculated.totalCost / (details.exp * craftAmount)).toLocaleString()}</p>
          <p><span className="item-title">Exp/Energy:</span>  {((details.exp * craftAmount) / calculated.energyCost).toLocaleString()}</p>
        </div>
      </div>

      <div>
        <h3 className="font-bold text-lg mt-4">Materials</h3>
        <ul className="material-list">
          {calculated.items.map((item, idx) => (
            <li key={idx} onClick={() => handleOpenModal(item.id)} className="cursor-pointer py-1 flex justify-between items-center">
              <div>
                <img src={item.image} alt={item.name} className="inline w-5 h-5" /> {item.name} × {item.quantity}
              </div>
              <div>{item.totalCost.toLocaleString()} coins</div>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-4 border-t pt-2 space-y-1" style={{ borderTopColor: "var(--entity-hover-bg-color)" }}>
        <div className="flex justify-between font-semibold">
          <span>Item Cost</span>
          <span>{calculated.totalItemCost.toLocaleString()} coins</span>
        </div>
        <div className="flex justify-between font-semibold">
          <span>Energy Cost</span>
          <span>
            {calculated.energyCostTotal.toLocaleString()} coins ({calculated.energyCost.toLocaleString()} ⚡)
          </span>
        </div>
        <div className="flex justify-between font-bold text-sm">
          <span>Total Cost</span>
          <span>{calculated.totalCost.toLocaleString()} coins</span>
        </div>
        <div className="flex justify-between font-semibold text-xs">
          <span>Market Price</span>
          <span>{calculated.marketCost.toLocaleString()} coins</span>
        </div>
        <div className="flex justify-between font-bold text-base border-t" style={{ borderTopColor: "var(--entity-hover-bg-color)" }}>
          <span>Total Profit</span>
          <span>{calculated.totalProfit.toLocaleString()} coins</span>
        </div>
      </div>
    </div>
  );
}
